import { uniqueId } from "lodash";
import { handleError, sanitizeFileName } from "@/utils";
import type { AxiosPromise } from "axios";
import FilesService from "@/services/Files";
import { uploadEventBus } from "@/buses/uploadEventBus";
import resumableUploadWorker from "@/workers/resumableUploadWorkerInstance";
import type {
    EventName,
    GoogleUploadFieldName,
    GoogleUploadModelName,
} from "@/types";
import { ref } from "vue";

const fileService = FilesService.getInstance();

/**
 * Composable for handling Google resumable uploads
 *
 */
export const useGoogleUpload = () => {
    const uploadedData = ref<any>(null);

    /**
     * Creates a resumable upload session for a single file
     *
     * @param {File} file - The file to be uploaded
     * @returns {Promise<Object>} Session data combined with the original file
     *   - token: string - Authentication token for the upload
     *   - url: string - Signed URL for the upload
     *   - file: File - Original file object
     * @private
     */
    const createUploadSession = async (
        file: File,
        modelName: GoogleUploadModelName,
        fieldName: GoogleUploadFieldName
    ) => {
        const { name: fileName, size, type: contentType } = file;

        const { data } = await fileService.createResumableUploadSession({
            model_name: modelName,
            field_name: fieldName,
            file_name: fileName,
            size,
            content_type: contentType,
        });

        uploadedData.value = data;

        return { ...data, file };
    };

    /**
     * Initiates the upload process by sending file data to the Web Worker
     *
     * @param {Worker} worker - The Web Worker instance handling the upload
     * @param {File} file - The file to be uploaded
     * @param {string} token - Authentication token for the upload
     * @param {string} signedUrl - The signed URL where the file should be uploaded
     * @private
     */
    const startUploadProcess = async (
        file: File,
        token: string,
        signedUrl: string,
        modelId: string,
        params: object,
        eventName: EventName | null,
        callback: (modeId: string, payload: object) => AxiosPromise<any>
    ) => {
        // create a callback id
        const callbackId = uniqueId(sanitizeFileName(file?.name ?? "File"));
        // Register the callback with the event bus
        uploadEventBus.addCallback(callbackId, callback);

        resumableUploadWorker.postMessage({
            sessionUri: signedUrl,
            token,
            file,
            modelId,
            callbackId,
            params,
            eventName,
        });
    };

    /**
     * Handles the upload of multiple documents
     *
     * @param {File[]} documents - Array of files to be uploaded
     * @returns {Promise<void>}
     * @throws Will throw an error if the upload process fails
     */
    const uploadDocuments = async (
        documents: File[],
        modelName: GoogleUploadModelName,
        fieldName: GoogleUploadFieldName,
        modelId: string,
        callback: (modeId: string, payload: object) => AxiosPromise<any>,
        params = {},
        eventName: EventName | null = null
    ) => {
        try {
            // Create upload session for each file.
            const uploadSessionsRequests = Array.from(documents).map(
                (file: File) => createUploadSession(file, modelName, fieldName)
            );

            // Wait for all upload sessions to be created.
            const uploadSessions = await Promise.all(uploadSessionsRequests);

            // Start the upload process for each session.
            uploadSessions.forEach((session) => {
                startUploadProcess(
                    session.file,
                    session.token,
                    session.url,
                    modelId,
                    params,
                    eventName,
                    callback
                );
            });
        } catch (error) {
            uploadEventBus.removeCallback(modelId);
            handleError(error);
        }
    };

    const uploadDocumentToGoogle = async (
        file: File,
        modelName: GoogleUploadModelName,
        fieldName: GoogleUploadFieldName,
        category: string
    ): Promise<{ fileName: string; category: string }> => {
        const { name, size, type: contentType } = file;
        const fileName = sanitizeFileName(name);
        const { data } = await fileService.createResumableUploadSession({
            model_name: modelName,
            field_name: fieldName,
            file_name: fileName,
            size,
            content_type: contentType,
        });

        const newFileName = data.file_name;
        const googleStorageUploadUrl = data.url;

        const response = await fetch(googleStorageUploadUrl, {
            method: "PUT",
            headers: {
                "Content-Type": file.type,
                "Content-Range": `bytes 0-${file.size - 1}/${file.size}`,
            },
            body: file,
        });

        if (!response.ok) {
            throw new Error(
                `File upload failed: ${response.status} ${response.statusText}`
            );
        }

        return { fileName: newFileName, category };
    };

    return {
        uploadDocuments,
        uploadDocumentToGoogle,
        resumableUploadWorker,
        uploadedData,
    };
};
